/**
 * @fileoverview Dismissed UI Elements Hook
 * 
 * Manages user dismissal state for UI elements like banners, tooltips, notices,
 * and onboarding prompts. Allows elements to be permanently dismissed per user,
 * improving UX by not repeatedly showing information the user has acknowledged.
 * 
 * Element Types:
 * - banner: Top-of-page informational banners
 * - tooltip: Contextual help tooltips
 * - notice: Educational notices and tips
 * - onboarding: First-time user guidance
 * - announcement: Product updates and news
 * - warning: Non-critical warnings that can be dismissed
 * 
 * Context:
 * Elements can include optional context to track dismissal of specific instances.
 * For example, a "feature announcement" banner might have context: { feature: 'milestones' }
 * to track dismissal separately from other announcements.
 * 
 * Use Cases:
 * - Onboarding tooltips shown only once
 * - Product announcements dismissed per user
 * - Educational notices that can be hidden
 * - Beta feature warnings
 * - Contextual help that can be turned off
 * 
 * Key Features:
 * - Per-user dismissal tracking
 * - Optional context for element-specific dismissal
 * - React Query integration for caching
 * - Optimistic updates on dismiss action
 * - Loading and mutation states
 * 
 * @module useDismissedUIElements
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

/**
 * Hook for managing dismissal state of UI elements
 * 
 * Tracks whether a user has dismissed a specific UI element. Dismissals are
 * stored in the dismissed_ui_elements table with the user ID, element type,
 * and optional context for granular tracking.
 * 
 * Query Behavior:
 * - Automatically checks dismissal status on mount
 * - Returns false when user is not authenticated
 * - Caches results with React Query
 * - Enabled only when user is authenticated
 * 
 * Mutation Behavior:
 * - Optimistic updates for instant UI feedback
 * - Invalidates query cache after successful dismiss
 * - Idempotent (safe to call multiple times)
 * 
 * Context Matching:
 * The context parameter allows tracking dismissal of specific instances of
 * an element type. The database uses JSONB containment (@>) to match context,
 * so dismissal applies to elements with matching or superset context.
 * 
 * @param elementType - Type identifier for the UI element (e.g., 'onboarding_tooltip')
 * @param context - Optional context object for specific element instances
 * @returns Object with dismissal state, loading state, and dismiss function
 * 
 * @example
 * ```tsx
 * function OnboardingTooltip({ feature }) {
 *   const {
 *     isDismissed,
 *     isLoading,
 *     dismiss,
 *     isDismissing
 *   } = useDismissedUIElements('onboarding_tooltip', { feature });
 * 
 *   if (isLoading || isDismissed) return null;
 * 
 *   return (
 *     <Tooltip>
 *       <TooltipContent>
 *         <p>Welcome to {feature}! Here's how it works...</p>
 *         <Button
 *           onClick={() => dismiss()}
 *           disabled={isDismissing}
 *         >
 *           Got it, don't show again
 *         </Button>
 *       </TooltipContent>
 *     </Tooltip>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Simple banner without context
 * function AnnouncementBanner() {
 *   const { isDismissed, dismiss } = useDismissedUIElements('announcement_banner');
 * 
 *   if (isDismissed) return null;
 * 
 *   return (
 *     <Banner>
 *       <BannerContent>New features available!</BannerContent>
 *       <Button onClick={() => dismiss()}>Dismiss</Button>
 *     </Banner>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Educational notice with specific context
 * function MilestoneEducationalNotice() {
 *   const { isDismissed, dismiss } = useDismissedUIElements(
 *     'educational_notice',
 *     { topic: 'milestones', page: 'calendar' }
 *   );
 * 
 *   if (isDismissed) return null;
 * 
 *   return (
 *     <Notice>
 *       <NoticeContent>
 *         Milestones help track project progress...
 *       </NoticeContent>
 *       <IconButton onClick={() => dismiss()}>
 *         <X />
 *       </IconButton>
 *     </Notice>
 *   );
 * }
 * ```
 */
export const useDismissedUIElements = (elementType: string, context?: any) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Query to check if element has been dismissed
   * 
   * Queries dismissed_ui_elements table for matching record with:
   * - Current user's ID
   * - Specified element type
   * - Matching or superset context (if provided)
   * 
   * Returns true if a matching dismissal record exists, false otherwise.
   * Error code PGRST116 (no rows) is treated as not dismissed.
   */
  const { data: isDismissed, isLoading } = useQuery({
    queryKey: ['dismissed-ui-elements', user?.id, elementType, context],
    queryFn: async () => {
      if (!user) return false;

      const { data, error } = await supabase
        .from('dismissed_ui_elements')
        .select('id')
        .eq('user_id', user.id)
        .eq('element_type', elementType)
        .contains('element_context', context || {})
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      return !!data;
    },
    enabled: !!user,
  });

  /**
   * Mutation to dismiss the UI element
   * 
   * Inserts a record into dismissed_ui_elements table marking this element
   * as dismissed for the current user. After successful insertion, the query
   * cache is invalidated to update the UI.
   * 
   * Idempotency:
   * The database has a unique constraint on (user_id, element_type, element_context),
   * so attempting to dismiss twice will fail gracefully without creating duplicates.
   * 
   * Error Handling:
   * - Throws if user is not authenticated
   * - Throws if database insert fails
   * - Errors should be caught by calling component
   */
  const { mutate: dismiss, isPending: isDismissing } = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');

      // Use upsert with ignoreDuplicates to make this idempotent
      const { error } = await supabase
        .from('dismissed_ui_elements')
        .upsert({
          user_id: user.id,
          element_type: elementType,
          element_context: context || {},
        }, {
          onConflict: 'user_id,element_type,element_context',
          ignoreDuplicates: true
        });

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate query to update isDismissed state
      queryClient.invalidateQueries({
        queryKey: ['dismissed-ui-elements', user?.id, elementType, context],
      });
    },
  });

  return {
    isDismissed: isDismissed ?? false,
    isLoading,
    dismiss,
    isDismissing,
  };
};